"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeLink = exports.upsertLink = exports.isLinkActive = exports.getActiveLink = exports.normalizeLinkHref = exports.hasRangeSelection = exports.getLinkAttributes = void 0;
const slate_1 = require("slate");
const isText = (node) => slate_1.Text.isText(node);
const getLinkAttributes = (node) => {
    const value = node?.a;
    if (!value || typeof value !== 'object')
        return null;
    const href = typeof value.href === 'string' ? value.href.trim() : '';
    if (!href)
        return null;
    const title = typeof value.title === 'string' ? value.title.trim() : undefined;
    return title ? { href, title } : { href };
};
exports.getLinkAttributes = getLinkAttributes;
const getTextEntries = (editor, at) => Array.from(slate_1.Editor.nodes(editor, {
    at: at ?? [],
    match: (node) => isText(node),
}));
const getEntryIndex = (entries, path) => entries.findIndex(([, entryPath]) => slate_1.Path.equals(entryPath, path));
const getExpandedLinkRange = (editor, entries, index, href) => {
    let left = index;
    let right = index;
    while (left > 0) {
        const link = (0, exports.getLinkAttributes)(entries[left - 1][0]);
        if (!link || link.href !== href)
            break;
        left--;
    }
    while (right < entries.length - 1) {
        const link = (0, exports.getLinkAttributes)(entries[right + 1][0]);
        if (!link || link.href !== href)
            break;
        right++;
    }
    return {
        anchor: slate_1.Editor.start(editor, entries[left][1]),
        focus: slate_1.Editor.end(editor, entries[right][1]),
    };
};
const hasRangeSelection = (editor) => {
    const { selection } = editor;
    return !!selection && !slate_1.Range.isCollapsed(selection);
};
exports.hasRangeSelection = hasRangeSelection;
const normalizeLinkHref = (href) => {
    const value = href.trim();
    if (!value)
        return '';
    if (/^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('/') || value.startsWith('#') || value.startsWith('?')) {
        return value;
    }
    return `https://${value}`;
};
exports.normalizeLinkHref = normalizeLinkHref;
const getActiveLink = (editor) => {
    const { selection } = editor;
    if (!selection)
        return null;
    if (slate_1.Range.isCollapsed(selection)) {
        const entries = getTextEntries(editor);
        const index = getEntryIndex(entries, selection.anchor.path);
        if (index < 0)
            return null;
        const link = (0, exports.getLinkAttributes)(entries[index][0]);
        if (!link)
            return null;
        const range = getExpandedLinkRange(editor, entries, index, link.href);
        return {
            href: link.href,
            title: link.title,
            range,
            text: slate_1.Editor.string(editor, range),
        };
    }
    const selectionRange = slate_1.Editor.unhangRange(editor, selection);
    const selectedEntries = getTextEntries(editor, selectionRange);
    if (!selectedEntries.length)
        return null;
    let link = null;
    for (const [node] of selectedEntries) {
        const currentLink = (0, exports.getLinkAttributes)(node);
        if (!currentLink)
            return null;
        if (!link) {
            link = currentLink;
            continue;
        }
        if (link.href !== currentLink.href)
            return null;
    }
    if (!link)
        return null;
    const entries = getTextEntries(editor);
    const index = getEntryIndex(entries, selectedEntries[0][1]);
    if (index < 0)
        return null;
    const range = getExpandedLinkRange(editor, entries, index, link.href);
    return {
        href: link.href,
        title: link.title,
        range,
        text: slate_1.Editor.string(editor, range),
    };
};
exports.getActiveLink = getActiveLink;
const isLinkActive = (editor) => !!(0, exports.getActiveLink)(editor);
exports.isLinkActive = isLinkActive;
const upsertLink = (editor, href) => {
    const nextHref = (0, exports.normalizeLinkHref)(href);
    if (!nextHref)
        return null;
    const activeLink = (0, exports.getActiveLink)(editor);
    const targetRange = activeLink?.range ?? ((0, exports.hasRangeSelection)(editor) ? slate_1.Editor.unhangRange(editor, editor.selection) : null);
    if (!targetRange)
        return null;
    const link = activeLink?.title ? { href: nextHref, title: activeLink.title } : { href: nextHref };
    slate_1.Transforms.setNodes(editor, { a: link }, {
        at: targetRange,
        match: (node) => isText(node),
        split: true,
    });
    if (editor.selection && slate_1.Range.isCollapsed(editor.selection))
        slate_1.Editor.addMark(editor, 'a', link);
    return (0, exports.getActiveLink)(editor);
};
exports.upsertLink = upsertLink;
const removeLink = (editor, at) => {
    if (at) {
        slate_1.Transforms.unsetNodes(editor, 'a', {
            at,
            match: (node) => isText(node),
            split: true,
        });
        if (editor.selection && slate_1.Range.isCollapsed(editor.selection))
            slate_1.Editor.removeMark(editor, 'a');
        return true;
    }
    const activeLink = (0, exports.getActiveLink)(editor);
    if (activeLink) {
        slate_1.Transforms.unsetNodes(editor, 'a', {
            at: activeLink.range,
            match: (node) => isText(node),
            split: true,
        });
        if (editor.selection && slate_1.Range.isCollapsed(editor.selection))
            slate_1.Editor.removeMark(editor, 'a');
        return true;
    }
    if (!(0, exports.hasRangeSelection)(editor))
        return false;
    slate_1.Editor.removeMark(editor, 'a');
    return true;
};
exports.removeLink = removeLink;
